import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralAgileMeetings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");
  const [meetings, setMeetings] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    title: "",
    meetingType: "Daily Standup",
    agenda: "",
    meetingDate: "",
    meetingTime: "",
    location: "",
    status: "Scheduled",
  };

  const [formData, setFormData] = useState(initialForm);

  // -----------------------
  // SAMPLE DATA
  // -----------------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
    { id: 3, name: "Customer Portal" },
  ];

  const demoMeetings = [
    {
      id: 1,
      projectId: 1,
      title: "Sprint Planning",
      meetingType: "Sprint Planning",
      agenda: "Define sprint backlog",
      meetingDate: "2026-03-10",
      meetingTime: "10:00",
      location: "Zoom",
      status: "Completed",
    },
    {
      id: 2,
      projectId: 1,
      title: "Daily Standup",
      meetingType: "Daily Standup",
      agenda: "Team updates",
      meetingDate: "2026-03-11",
      meetingTime: "09:30",
      location: "Teams",
      status: "Scheduled",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setMeetings(demoMeetings);
  };

  // -----------------------
  // CRUD
  // -----------------------

  const handleAdd = () => {
    if (!selectedProjectId) {
      alert("Please select a project first.");
      return;
    }

    setEditingMeeting(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (meeting) => {
    setEditingMeeting(meeting);

    setFormData({
      title: meeting.title,
      meetingType: meeting.meetingType,
      agenda: meeting.agenda,
      meetingDate: meeting.meetingDate,
      meetingTime: meeting.meetingTime,
      location: meeting.location,
      status: meeting.status,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this meeting?")) return;

    setMeetings((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.meetingDate || !formData.meetingTime) {
      alert("Please select meeting date and time.");
      return;
    }

    const newMeeting = {
      id: editingMeeting?.id || Date.now(),
      projectId: Number(selectedProjectId),
      ...formData,
    };

    if (editingMeeting) {
      setMeetings((prev) =>
        prev.map((m) => (m.id === editingMeeting.id ? newMeeting : m)),
      );
    } else {
      setMeetings((prev) => [...prev, newMeeting]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // -----------------------
  // FILTERS
  // -----------------------

  const projectMeetings = meetings.filter(
    (m) => String(m.projectId) === String(selectedProjectId),
  );

  const filtered = projectMeetings.filter(
    (m) =>
      m.title.toLowerCase().includes(search.toLowerCase()) ||
      (m.agenda || "").toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  const statusColor = {
    Scheduled: "bg-indigo-600",
    Completed: "bg-emerald-600",
    Cancelled: "bg-red-600",
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">
      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            🤝 SprintCentral Agile Meetings
          </div>
          <div className="text-xs text-gray-400">
            Manage Agile Ceremonies & Team Sync
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>

              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      </header>

      <div className="mt-4 min-h-[75vh]">
        {/* ACTION BAR */}

        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Meetings
          </button>

          <button
            onClick={handleAdd}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Meeting
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>

              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              placeholder="Search meetings..."
              value={search}
              style={{ width: "100%" }}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />
          </div>
        </div>

        {/* MODAL */}

        {showModal && (
          <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg text-cyan-200 font-semibold">
                {editingMeeting ? "Edit Meeting" : "New Meeting"}
              </h2>

              <button
                className="w-12 bg-blue-500"
                onClick={() => setShowModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Meeting Title"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <select
                value={formData.meetingType}
                onChange={(e) =>
                  setFormData({ ...formData, meetingType: e.target.value })
                }
                className="w-48 h-12 bg-gray-900 border border-gray-700 rounded p-2"
              >
                <option>Daily Standup</option>
                <option>Sprint Planning</option>
                <option>Sprint Review</option>
                <option>Sprint Retrospective</option>
                <option>Backlog Refinement</option>
              </select>

              <textarea
                placeholder="Meeting Agenda"
                style={{ height: "100px", width: "100%" }}
                value={formData.agenda}
                onChange={(e) =>
                  setFormData({ ...formData, agenda: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="date"
                  value={formData.meetingDate}
                  onChange={(e) =>
                    setFormData({ ...formData, meetingDate: e.target.value })
                  }
                  className="w-48 bg-white border border-gray-700 rounded p-2 text-black"
                />

                <input
                  type="time"
                  value={formData.meetingTime}
                  onChange={(e) =>
                    setFormData({ ...formData, meetingTime: e.target.value })
                  }
                  className="ml-2 w-48 bg-white border border-gray-700 rounded p-2 text-black"
                />
              </div>

              <input
                type="text"
                placeholder="Location / Meeting Link"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
              >
                {editingMeeting ? "Update Meeting" : "Create Meeting"}
              </button>
            </form>
          </div>
        )}

        {/* MEETING CARDS */}

        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">
          {paginated.map((meeting) => (
            <div
              key={meeting.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >
              <div className="flex justify-between items-start">
                <h3 className="font-semibold text-white">{meeting.title}</h3>

                <span
                  className={`px-2 py-1 rounded text-xs ${statusColor[meeting.status]}`}
                >
                  {meeting.status}
                </span>
              </div>

              <p className="text-xs text-cyan-300 mt-1">
                {meeting.meetingType}
              </p>

              <p className="text-sm text-gray-300 mt-2">
                {meeting.agenda || "No agenda"}
              </p>

              <div className="text-xs text-gray-400 mt-3">
                Date: {meeting.meetingDate} <br />
                Time: {meeting.meetingTime}
              </div>

              <div className="text-xs text-gray-400">
                Location: {meeting.location || "-"}
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleEdit(meeting)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>

                <button
                  onClick={() => handleDelete(meeting.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintCentralAgileMeetings;
